import React from 'react';
import { isNil } from 'lodash';

import type {
  CoveredItemRow,
  InvoiceAnalysisResponse,
  InvoiceData,
  LineItemRow,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import mixpanel from '~/components/CmsMain/mixpanel';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { CLIVE_MIXPANEL_EVENTS } from '~/pocs/mixpanel';

interface FooterProps {
  disabled: boolean;
  submitting: boolean;
  onClose: () => void;
  onSave: (data: InvoiceAnalysisResponse) => Promise<boolean>;
  onSaveAndFinish?: (data: InvoiceAnalysisResponse) => Promise<boolean>;
  lineItems: LineItemRow[];
  aggregateCoverages: CoveredItemRow[];
  invoiceData: InvoiceData;
  saveDisabledMessage?: string;
}

export const Footer: React.FC<FooterProps> = ({
  disabled,
  submitting,
  onClose,
  onSave,
  onSaveAndFinish,
  aggregateCoverages,
  lineItems,
  invoiceData,
  saveDisabledMessage,
}) => {
  const shouldDisplaySaveAndFinish = !isNil(onSaveAndFinish);

  const getData = (): InvoiceAnalysisResponse => ({
    line_items: lineItems,
    coverages: aggregateCoverages,
    invoice_data: invoiceData,
  });

  return (
    <ButtonsContainer>
      {disabled ? (
        <Button
          disabled={submitting}
          variant="outlined"
          color="primary"
          className="mr-20"
          onClick={() => {
            mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_CLOSE_CLICKED);
            onClose();
          }}
        >
          Close
        </Button>
      ) : (
        <>
          <Tooltip title={saveDisabledMessage || ''} disabled={!saveDisabledMessage}>
            <span>
              <Button
                disabled={disabled || !!saveDisabledMessage || submitting}
                variant={shouldDisplaySaveAndFinish ? 'outlined' : 'contained'}
                color="primary"
                className="mr-20"
                onClick={async () => {
                  mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_SAVE_CLICKED);
                  if (await onSave(getData())) {
                    onClose();
                  }
                }}
              >
                Save
              </Button>
            </span>
          </Tooltip>
          {shouldDisplaySaveAndFinish && (
            <Tooltip title={saveDisabledMessage || ''} disabled={!saveDisabledMessage}>
              <span>
                <Button
                  disabled={disabled || !!saveDisabledMessage || submitting}
                  variant="contained"
                  color="primary"
                  className="mr-20"
                  onClick={async () => {
                    mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_SAVE_AND_FINISH_CLICKED);
                    if (await onSaveAndFinish(getData())) {
                      onClose();
                    }
                  }}
                >
                  Save & Finish
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      )}
    </ButtonsContainer>
  );
};
